import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_BLOOMIE_BY_EMAIL } from '@bloomays-lib/adapter.api-bloomer';
import type { ApolloGqlAccountManager } from '@bloomays-lib/types.shared';
import { Logger } from '../services/service-logger';
import { useBloomiesStore } from '../stores/bloomiesStore';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';

const logger = Logger('useBloomies');

type GetBloomieByEmailResponse = {
  getBloomieByEmail: ApolloGqlAccountManager | null;
};

type UseBloomiesParams = {
  email: string;
};

export const useBloomies = ({ email }: UseBloomiesParams) => {
  const { getByEmail, setBloomie, mustRefreshBloomie } = useBloomiesStore();
  const [fetchBloomie, { data, loading, error, refetch }] = useLazyQuery<GetBloomieByEmailResponse>(
    GET_BLOOMIE_BY_EMAIL,
    {
      context: { clientName: 'api.bloomers' },
      fetchPolicy: 'cache-and-network',
      variables: { email },
    },
  );

  const cached = getByEmail(email);

  // Déclenche le fetch quand l'email change si le cache est manquant ou expiré
  useEffect(() => {
    if (email) {
      const shouldFetch = mustRefreshBloomie(email);
      if (shouldFetch) {
        logger.debug('Fetching bloomie by email', { email });
        void fetchBloomie();
      }
    }
  }, [email, fetchBloomie, mustRefreshBloomie]);

  // Gestion des erreurs
  useEffect(() => {
    if (error) {
      logger.error('GraphQL error', { email }, error);
      monitorException(error, { name: 'useBloomies', email });
    }
  }, [error, email]);

  // Persiste la donnée récupérée dans le store
  useEffect(() => {
    if (email && data?.getBloomieByEmail) {
      setBloomie(email, data.getBloomieByEmail);
    }
  }, [email, data, setBloomie]);

  return {
    bloomie: cached || data?.getBloomieByEmail || null,
    loading: loading && !cached,
    error,
    refetch,
  };
};

export default useBloomies;
